import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { canExecuteCheckoutSplitAgentForTeam } from "./schema/ab-tests/can-execute-checkout-split-agent-for-team";
import { canStartCheckoutAbTest } from "./schema/ab-tests/can-start-checkout-ab-test";
import { concludeCheckoutAbTest } from "./schema/ab-tests/conclude-checkout-ab-test";
import { createCheckoutSplitFromBuilder } from "./schema/ab-tests/create-checkout-split-from-builder";
import { createCheckoutSplitFromFlowSteps } from "./schema/ab-tests/create-checkout-split-from-flow-steps";
import { createProspectiveCheckoutSplitFromFlowSteps } from "./schema/ab-tests/create-prospective-checkout-split-from-flow-steps";
import { deleteScheduledAbTest } from "./schema/ab-tests/delete-scheduled-ab-test";
import { executeCheckoutSplitAgent } from "./schema/ab-tests/execute-checkout-split-agent";
import { getCheckoutSplitDetails } from "./schema/ab-tests/get-checkout-split-details";
import { getConcludedAndActiveCheckoutSplits } from "./schema/ab-tests/get-concluded-and-active-checkout-splits";
import { getScheduledSplitTrees } from "./schema/ab-tests/get-scheduled-split-trees";
import { getSplitLibrarySplits } from "./schema/ab-tests/get-split-library-splits";
import { getUpcomingCheckoutSplitsInOrder } from "./schema/ab-tests/get-upcoming-checkout-splits-in-order";
import { setUpcomingCheckoutSplitsOrder } from "./schema/ab-tests/set-upcoming-checkout-splits-order";
import { startScheduledCheckoutAbTest } from "./schema/ab-tests/start-scheduled-checkout-ab-test";
import { unsafeDeleteCheckoutAbTest } from "./schema/ab-tests/unsafe-delete-checkout-ab-test";
import { validateCheckoutTreeTransformation } from "./schema/ab-tests/validate-checkout-tree-transformation";
import { validateCheckoutTreeTransformationForNewSplit } from "./schema/ab-tests/validate-checkout-tree-transformation-for-new-split";
import { addStorefrontDeliveryEstimateSelector } from "./schema/add-storefront-delivery-estimate-selector";
import { calculateCheckoutSplitResults } from "./schema/analytics/calculate-checkout-split-results";
import { getAllCheckoutSplitMetrics } from "./schema/analytics/get-all-checkout-split-metrics";
import { getCheckoutOptimizationAnalyticsAssumptions } from "./schema/analytics/get-checkout-optimization-analytics-assumptions";
import { getCheckoutOptimizationRevenuePerCheckoutExperienceMetrics } from "./schema/analytics/get-checkout-optimization-revenue-per-checkout-experience-metrics";
import { getCheckoutOptimizationSummaryMetrics } from "./schema/analytics/get-checkout-optimization-summary-metrics";
import { getCheckoutOptimizationTimeSeriesMetrics } from "./schema/analytics/get-checkout-optimization-time-series-metrics";
import { getCheckoutProbabilityMeasurementMetrics } from "./schema/analytics/get-checkout-probability-measurement-metrics";
import { getCheckoutSplitAiAnalysis } from "./schema/analytics/get-checkout-split-ai-analysis";
import { getCheckoutSplitSummaryMetrics } from "./schema/analytics/get-checkout-split-summary-metrics";
import { getImpactDashboardMetrics } from "./schema/analytics/get-impact-dashboard-metrics";
import { getMercuryAnalyticsForCheckoutExperience } from "./schema/analytics/get-mercury-analytics-for-checkout-experience";
import { getMercurySummaryAnalytics } from "./schema/analytics/get-mercury-summary-analytics";
import { getOnlineStoreOrderCounts } from "./schema/analytics/get-online-store-order-counts";
import { getRecentRescuePanelSplitData } from "./schema/analytics/get-recent-rescue-panel-split-data";
import { getRescuePanelAttribution } from "./schema/analytics/get-rescue-panel-attribution";
import { getSegmentedCheckoutMetrics } from "./schema/analytics/get-segmented-checkout-metrics";
import { getSegmentedCheckoutMetricsForSplit } from "./schema/analytics/get-segmented-checkout-metrics-for-split";
import { getSegmentedCheckoutMetricsInsights } from "./schema/analytics/get-segmented-checkout-metrics-insights";
import { getThresholdUpsellWidgetAnalytics } from "./schema/analytics/get-threshold-upsell-widget-analytics";
import { getWidgetProductAnalytics } from "./schema/analytics/get-widget-product-analytics";
import { getWidgetsSummaryMetrics } from "./schema/analytics/get-widgets-summary-metrics";
import { getWidgetsTimeSeriesMetrics } from "./schema/analytics/get-widgets-time-series-metrics";
import { sendScheduleDemoClickedNotification } from "./schema/analytics/send-schedule-demo-clicked-notification";
import { updateCheckoutOptimizationAnalyticsAssumptions } from "./schema/analytics/update-checkout-optimization-analytics-assumptions";
import { generatePdfForInvoice } from "./schema/billing/generate-pdf-for-invoice";
import { getCurrentCheckoutOptimizationInvoice } from "./schema/billing/get-current-checkout-optimization-invoice";
import { getFinalizedServiceInvoices } from "./schema/billing/get-finalized-service-invoices";
import { addUpsellPageToCheckoutExperience } from "./schema/checkout-experiences/add-upsell-page-to-checkout-experience";
import { assignWidgetsToExperience } from "./schema/checkout-experiences/assign-widgets-to-experience";
import { canSetCheckoutExperienceActive } from "./schema/checkout-experiences/can-set-checkout-experience-active";
import { constrainRateTableRelativeToOtherRateTable } from "./schema/checkout-experiences/constrain-rate-table-relative-to-other-rate-table";
import { createCheckoutExperience } from "./schema/checkout-experiences/create-checkout-experience";
import { createCheckoutWidget } from "./schema/checkout-experiences/create-checkout-widget";
import { deleteCheckoutExperience } from "./schema/checkout-experiences/delete-checkout-experience";
import { duplicateCheckoutExperience } from "./schema/checkout-experiences/duplicate-checkout-experience";
import { getAllMercuryModelsForCheckoutExperience } from "./schema/checkout-experiences/get-all-mercury-models-for-checkout-experience";
import { getCheckoutExperienceById } from "./schema/checkout-experiences/get-checkout-experience-by-id";
import { getCheckoutExperienceParcelTreeFlowSteps } from "./schema/checkout-experiences/get-checkout-experience-parcel-tree-flow-steps";
import { getCheckoutExperiences } from "./schema/checkout-experiences/get-checkout-experiences";
import { getCheckoutExperiencesForAbTestBuilder } from "./schema/checkout-experiences/get-checkout-experiences-for-ab-test-builder";
import { getCheckoutWidgets } from "./schema/checkout-experiences/get-checkout-widgets";
import { getRateTableNameAndDescriptionById } from "./schema/checkout-experiences/get-rate-table-name-and-description-by-id";
import { migrateCheckoutExperienceToConstraintsV2 } from "./schema/checkout-experiences/migrate-checkout-experience-to-constraints-v2";
import { removeUpsellPageFromCheckoutExperience } from "./schema/checkout-experiences/remove-upsell-page-from-checkout-experience";
import { setCheckoutExperienceActive } from "./schema/checkout-experiences/set-checkout-experience-active";
import { setShippingMethodOrder } from "./schema/checkout-experiences/set-shipping-method-order";
import { updateCheckoutExperience } from "./schema/checkout-experiences/update-checkout-experience";
import { updateCheckoutParcelTree } from "./schema/checkout-experiences/update-checkout-parcel-tree";
import { updateCheckoutWidget } from "./schema/checkout-experiences/update-checkout-widget";
import { updateMercuryModelAssumedOrderMargin } from "./schema/checkout-experiences/update-mercury-model-assumed-order-margin";
import { getCheckoutProbabilityRescueModalSettings } from "./schema/get-checkout-probability-rescue-modal-settings";
import { getCheckoutTreeAnalytics } from "./schema/get-checkout-tree-analytics";
import { getCheckoutTreeFlowSteps } from "./schema/get-checkout-tree-flow-steps";
import { getCheckoutTreeFlowStepsForSplit } from "./schema/get-checkout-tree-flow-steps-for-split";
import { getFulfillmentCutoffSchedule } from "./schema/get-fulfillment-cutoff-schedule";
import { getOmsShippingMethodNameConsiderations } from "./schema/get-oms-shipping-method-name-considerations";
import { getStorefrontDeliveryEstimateSettings } from "./schema/get-storefront-delivery-estimate-settings";
import { getSubscriptionPreOrderDeliveryProfileSettings } from "./schema/get-subscription-pre-order-delivery-profile-settings";
import { deleteUpsellPage } from "./schema/post-purchase/delete-upsell-page";
import { getPostPurchaseFlowSteps } from "./schema/post-purchase/get-post-purchase-flow-steps";
import { getUpsellPages } from "./schema/post-purchase/get-upsell-pages";
import { updatePostPurchaseFlow } from "./schema/post-purchase/update-post-purchase-flow";
import { createPricingTest } from "./schema/pricing-tests/create-pricing-test";
import { endPricingTest } from "./schema/pricing-tests/end-pricing-test";
import { getPricingTestAnalytics } from "./schema/pricing-tests/get-pricing-test-analytics";
import { getPricingTests } from "./schema/pricing-tests/get-pricing-tests";
import { refreshSubscriptionPreOrderDeliveryProfile } from "./schema/refresh-subscription-pre-order-delivery-profile";
import { removeStorefrontDeliveryEstimateSelector } from "./schema/remove-storefront-delivery-estimate-selector";
import { updateCheckoutProbabilityRescueModalSettings } from "./schema/update-checkout-probability-rescue-modal-settings";
import { updateCheckoutTree } from "./schema/update-checkout-tree";
import { updateFulfillmentCutoffSchedule } from "./schema/update-fulfillment-cutoff-schedule";
import { updateProspectiveCheckoutTree } from "./schema/update-prospective-checkout-tree";
import { updateStorefrontDeliveryEstimateSelector } from "./schema/update-storefront-delivery-estimate-selector";
import { updateStorefrontDeliveryEstimateSettings } from "./schema/update-storefront-delivery-estimate-settings";
import { updateSubscriptionPreOrderDeliveryProfileSettings } from "./schema/update-subscription-pre-order-delivery-profile-settings";

export const rpcDefinition = {
  // A/B test
  canStartCheckoutAbTest,
  validateCheckoutTreeTransformation,
  validateCheckoutTreeTransformationForNewSplit,
  concludeCheckoutAbTest,
  createCheckoutSplitFromBuilder,
  createCheckoutSplitFromFlowSteps,
  createProspectiveCheckoutSplitFromFlowSteps,
  deleteScheduledAbTest,
  unsafeDeleteCheckoutAbTest,
  canExecuteCheckoutSplitAgentForTeam,
  executeCheckoutSplitAgent,
  getCheckoutSplitDetails,
  getConcludedAndActiveCheckoutSplits,
  getUpcomingCheckoutSplitsInOrder,
  setUpcomingCheckoutSplitsOrder,
  startScheduledCheckoutAbTest,
  getScheduledSplitTrees,
  getSplitLibrarySplits,
  // analytics
  calculateCheckoutSplitResults,
  getAllCheckoutSplitMetrics,
  getCheckoutSplitAiAnalysis,
  getCheckoutSplitSummaryMetrics,
  getCheckoutOptimizationAnalyticsAssumptions,
  getCheckoutOptimizationRevenuePerCheckoutExperienceMetrics,
  getCheckoutOptimizationSummaryMetrics,
  getCheckoutOptimizationTimeSeriesMetrics,
  getImpactDashboardMetrics,
  getCheckoutProbabilityMeasurementMetrics,
  getMercuryAnalyticsForCheckoutExperience,
  getMercurySummaryAnalytics,
  getOnlineStoreOrderCounts,
  getRecentRescuePanelSplitData,
  getRescuePanelAttribution,
  getThresholdUpsellWidgetAnalytics,
  getWidgetProductAnalytics,
  getWidgetsSummaryMetrics,
  getWidgetsTimeSeriesMetrics,
  updateCheckoutOptimizationAnalyticsAssumptions,
  getCheckoutTreeAnalytics,
  getSegmentedCheckoutMetrics,
  getSegmentedCheckoutMetricsForSplit,
  getSegmentedCheckoutMetricsInsights,
  sendScheduleDemoClickedNotification,
  // billing
  generatePdfForInvoice,
  getCurrentCheckoutOptimizationInvoice,
  getFinalizedServiceInvoices,
  // checkout extension
  addUpsellPageToCheckoutExperience,
  constrainRateTableRelativeToOtherRateTable,
  createCheckoutExperience,
  deleteCheckoutExperience,
  duplicateCheckoutExperience,
  getAllMercuryModelsForCheckoutExperience,
  getCheckoutExperienceById,
  migrateCheckoutExperienceToConstraintsV2,
  getCheckoutExperienceParcelTreeFlowSteps,
  getCheckoutExperiences,
  getCheckoutExperiencesForAbTestBuilder,
  createCheckoutWidget,
  getCheckoutWidgets,
  updateCheckoutWidget,
  assignWidgetsToExperience,
  getRateTableNameAndDescriptionById,
  removeUpsellPageFromCheckoutExperience,
  setCheckoutExperienceActive,
  setShippingMethodOrder,
  canSetCheckoutExperienceActive,
  updateCheckoutExperience,
  updateCheckoutParcelTree,
  updateMercuryModelAssumedOrderMargin,
  // post-purchase
  deleteUpsellPage,
  getPostPurchaseFlowSteps,
  updatePostPurchaseFlow,
  getUpsellPages,
  // pricing tests
  createPricingTest,
  endPricingTest,
  getPricingTestAnalytics,
  getPricingTests,
  // other
  getCheckoutTreeFlowSteps,
  getCheckoutTreeFlowStepsForSplit,
  getFulfillmentCutoffSchedule,
  getStorefrontDeliveryEstimateSettings,
  getSubscriptionPreOrderDeliveryProfileSettings,
  addStorefrontDeliveryEstimateSelector,
  updateStorefrontDeliveryEstimateSelector,
  removeStorefrontDeliveryEstimateSelector,
  updateFulfillmentCutoffSchedule,
  updateStorefrontDeliveryEstimateSettings,
  updateSubscriptionPreOrderDeliveryProfileSettings,
  refreshSubscriptionPreOrderDeliveryProfile,
  getCheckoutProbabilityRescueModalSettings,
  updateCheckoutProbabilityRescueModalSettings,
  updateCheckoutTree,
  updateProspectiveCheckoutTree,
  getOmsShippingMethodNameConsiderations,
};

export type RedoConversionRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
